import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../components/AuthContext';
import { isSuperuser } from '../utils/permissions';
import axios from 'axios';
import { useToast } from '../context/ToastContext';
import { useConfirmDialog } from '../context/ConfirmDialogContext';
import { useAsyncActionMap } from '../hooks/useAsyncAction';
import DataTable from '../components/DataTable';
import { formatDateTime, formatRelative } from '../utils/format';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function UsersPage() {
  const { t } = useTranslation();
  const { user, hasPermission } = useAuth();
  const toast = useToast();
  // ?tenant_id=… narrows the table to one company. Set by Firmalar
  // tab row deep-links so the operator lands on the tenant's users
  // pre-filtered. No-op (shows all) when query param absent.
  const [searchParams] = useSearchParams();
  const tenantFilter = searchParams.get('tenant_id') || '';
  const { confirm } = useConfirmDialog();
  const { executeWithId, isLoadingId } = useAsyncActionMap();
  const [users, setUsers] = useState([]);
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'user',
    level: 1,
    tenant_id: '',
    position_id: '',
    name: '',
    email: ''
  });

  const fetchLicenseInfo = async () => {
    // Superusers don't have a tenant scope; /me 400s, just skip the banner.
    if (user?.role === 'superuser') return;
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/api/billing/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLicenseInfo(data);
    } catch (e) {
      // silent — banner is best-effort
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    if (user?.role === 'superuser') {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/companies`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCompanies(response.data.filter(c => c.status === 'active'));
      } catch (error) {
        console.error('Failed to fetch companies:', error);
      }
    }
  };

  const fetchPositions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/hr/positions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPositions(response.data);
    } catch (error) {
      console.error('Failed to fetch positions:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
    fetchPositions();
    fetchLicenseInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // The superuser has no company, so /api/hr/positions returns every
  // tenant's positions: offer only those of the company being edited.
  const formPositions = isSuperuser(user)
    ? positions.filter((p) => formData.tenant_id && p.tenant_id === formData.tenant_id)
    : positions;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const permission = editingUser ? 'system.users.edit' : 'system.users.create';
    if (!hasPermission(permission)) {
      toast.error(t('common.errors.permissionDenied'));
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (editingUser) {
        const updateData = { username: formData.username, level: formData.level };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await axios.put(`${API_URL}/api/users/${editingUser.id}`, updateData, { headers });
      } else {
        const tenant_id = user?.role === 'admin' ? user.tenant_id : formData.tenant_id;
        await axios.post(`${API_URL}/api/users`, {
          ...formData,
          tenant_id
        }, { headers });
      }

      setShowModal(false);
      setEditingUser(null);
      setFormData({ username: '', password: '', role: 'user', level: 1, tenant_id: '', position_id: '', name: '', email: '' });
      fetchUsers();
    } catch (error) {
      // a user-limit refusal is shown by the axios interceptor with a billing link
      if (error.response?.headers?.['x-error-code'] !== 'user_limit_reached') {
        toast.error(error.response?.data?.detail || t('users.saveFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (u) => {
    if (!hasPermission('system.users.edit')) {
      toast.error(t('common.errors.permissionDenied'));
      return;
    }
    setEditingUser(u);
    setFormData({
      username: u.username,
      password: '',
      role: u.role,
      level: u.level || 1,
      tenant_id: u.tenant_id,
      position_id: u.position_id || '',
      name: u.name || '',
      email: u.email || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (target) => {
    const { id } = target;
    if (isLoadingId(`delete-${id}`)) return;

    if (!hasPermission('system.users.delete')) {
      toast.error(t('common.errors.permissionDenied'));
      return;
    }

    const confirmed = await confirm({
      title: t('sharedui.adminConfirm.deleteUserTitle'),
      message: t('sharedui.adminConfirm.deleteUser', { name: target.username || target.email || '' }),
      confirmText: t('sharedui.adminConfirm.deleteButton'),
      cancelText: t('sharedui.adminConfirm.cancelButton'),
      type: 'danger',
    });
    if (!confirmed) {
      return;
    }

    await executeWithId(`delete-${id}`, async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_URL}/api/users/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('users.deleted'));
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || t('users.deleteFailed'));
      }
    });
  };

  // Pasif yap / Aktif yap: a passive user cannot sign in, their tokens stop
  // working and their pending approvals move to others (PUT is_active).
  const handleToggleActive = async (target) => {
    const { id } = target;
    if (isLoadingId(`active-${id}`)) return;
    if (!hasPermission('system.users.edit')) {
      toast.error(t('common.errors.permissionDenied'));
      return;
    }
    const activate = target.is_active === false;
    const name = target.name || target.username || target.email || '';
    const confirmed = await confirm({
      title: t(activate ? 'users.activateTitle' : 'users.deactivateTitle'),
      message: t(activate ? 'users.activateMessage' : 'users.deactivateMessage', { name }),
      confirmText: t(activate ? 'users.activate' : 'users.deactivate'),
      cancelText: t('common.cancel'),
      type: activate ? 'info' : 'warning',
    });
    if (!confirmed) return;
    await executeWithId(`active-${id}`, async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API_URL}/api/users/${id}`, { is_active: activate }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(t(activate ? 'users.activated' : 'users.deactivated'));
        fetchUsers();
        fetchLicenseInfo();
      } catch (error) {
        toast.error(error.response?.data?.detail || t('users.statusFailed'));
      }
    });
  };
  // the own account and a superuser cannot be made passive (backend 403)
  const canToggleActive = (u) => u.id !== user?.id && u.role !== 'superuser';

  const getCompanyName = (tenantId) => {
    const company = companies.find(c => c.id === tenantId);
    return company ? company.name : user?.company_name || t('users.unknownCompany');
  };

  // Seat usage banner state — only meaningful for tenant admins. user_limit is
  // the limit the server enforces (trial: the platform's trial limit; null:
  // unlimited); an older backend only sends max_users.
  const seatMax = licenseInfo
    ? ('user_limit' in licenseInfo ? licenseInfo.user_limit : licenseInfo.max_users)
    : null;
  const showSeatBanner = !!licenseInfo && seatMax > 0
    && licenseInfo.users_used >= seatMax - 1
    && user?.role !== 'superuser';
  const seatExhausted = !!licenseInfo && seatMax > 0 && licenseInfo.users_used >= seatMax;
  // the server refuses POST /api/users then (a superuser may still add)
  const seatsFull = user?.role !== 'superuser' && !!licenseInfo
    && (licenseInfo.can_add_user === false || (licenseInfo.can_add_user === undefined && seatExhausted));

  const roleText = (u) => t(`users.role.${u.role}`, { defaultValue: u.role });
  const userColumns = [
    { key: 'username', header: t('users.username'), sortable: true,
      render: (u) => <span className="text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{u.username}</span> },
    { key: 'role', header: t('users.roleLabel'), sortable: true, value: roleText,
      render: (u) => (
        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
          u.role === 'admin'
            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
        }`}>
          {roleText(u)}
        </span>
      ) },
    { key: 'level', header: t('users.level'), sortable: true, value: (u) => u.level || 1,
      render: (u) => <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{t('users.levelValue', { level: u.level || 1 })}</span> },
    { key: 'is_active', header: t('users.status.label'), sortable: true,
      value: (u) => t(u.is_active === false ? 'users.status.passive' : 'users.status.active'),
      render: (u) => (
        <span data-testid={`user-status-${u.id}`} className={`badge ${u.is_active === false ? 'badge-neutral' : 'badge-success'} whitespace-nowrap`}>
          {t(u.is_active === false ? 'users.status.passive' : 'users.status.active')}
        </span>
      ) },
    ...(user?.role === 'superuser' ? [{
      key: 'tenant_id', header: t('users.company'), sortable: true, value: (u) => getCompanyName(u.tenant_id),
      render: (u) => <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{getCompanyName(u.tenant_id)}</span>,
    },
    // last login / last action: relative, the exact time on hover; the ISO
    // value keeps sorting chronological
    ...['last_login_at', 'last_seen_at'].map((key) => ({
      key, header: t(key === 'last_login_at' ? 'users.lastLogin' : 'users.lastSeen'), sortable: true,
      value: (u) => u[key] || '',
      render: (u) => (u[key] ? (
        <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap" title={formatDateTime(u[key])}
          data-testid={`user-${key}-${u.id}`}>
          {formatRelative(u[key])}
        </span>
      ) : <span className="text-sm text-gray-400">{t('users.never')}</span>),
    }))] : []),
  ];

  return (
    <div data-testid="users-page" className="space-y-6 fade-in">
      {showSeatBanner && (
        <div className={`rounded-lg p-4 border ${seatExhausted ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="text-sm">
              {seatExhausted ? (
                <>
                  <b className="text-red-800">{t('users.seatBanner.fullTitle')}</b>{' '}
                  <span className="text-red-700">{t('users.seatBanner.fullText', { used: licenseInfo.users_used, max: seatMax })}</span>
                </>
              ) : (
                <>
                  <b className="text-amber-800">{t('users.seatBanner.lastSeatTitle')}</b>{' '}
                  <span className="text-amber-700">{t('users.seatBanner.usage', { used: licenseInfo.users_used, max: seatMax })}</span>
                </>
              )}
            </div>
            <Link to="/modules/admin/billing?action=add-seats"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap">
              + {t('users.seatBanner.buySeats')}
            </Link>
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="page-title text-3xl mb-2">{t('users.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400">
            {user?.role === 'superuser' 
              ? t('users.subtitleAllCompanies')
              : t('users.subtitleOwnCompany')}
          </p>
        </div>
        <button
          data-testid="add-user-button"
          onClick={() => {
            setEditingUser(null);
            setFormData({
              username: '',
              password: '',
              role: 'user',
              level: 1,
              tenant_id: user?.role === 'admin' ? user.tenant_id : ''
            });
            setShowModal(true);
          }}
          disabled={seatsFull}
          title={seatsFull ? t('users.seatLimit.tooltip', { max: seatMax }) : undefined}
          className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          + {t('users.addUser')}
        </button>
      </div>

      {tenantFilter && (
        <div className="mb-3 bg-blue-50 border border-blue-200 text-blue-800 rounded px-3 py-2 text-sm flex items-center justify-between">
          <span>🏢 {t('users.filteredToOneCompany')}</span>
          <Link to="/users" className="text-xs underline hover:text-blue-900">{t('users.clearFilter')}</Link>
        </div>
      )}

      <DataTable
        id="system-users"
        rows={users.filter((u) => (!tenantFilter || u.tenant_id === tenantFilter)
          && (statusFilter === 'all' || (statusFilter === 'passive') === (u.is_active === false)))}
        loading={loading}
        rowKey="id"
        exportName="kullanicilar"
        emptyText={t('users.empty')}
        columns={userColumns}
        filters={(
          <select className="select !w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            aria-label={t('users.status.label')} data-testid="users-status-filter">
            <option value="all">{t('users.status.all')}</option>
            <option value="active">{t('users.status.active')}</option>
            <option value="passive">{t('users.status.passive')}</option>
          </select>
        )}
        rowActions={(u) => (
          <span className="inline-flex items-center text-sm font-medium">
            <button
              type="button"
              data-testid={`edit-user-${u.id}`}
              onClick={() => handleEdit(u)}
              className="text-primary-600 hover:text-primary-900 me-4"
            >
              {t('common.edit')}
            </button>
            {canToggleActive(u) && (
              <button
                type="button"
                data-testid={`toggle-active-${u.id}`}
                onClick={() => handleToggleActive(u)}
                disabled={isLoadingId(`active-${u.id}`)}
                className="text-amber-600 hover:text-amber-800 dark:text-amber-400 me-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {u.is_active === false ? t('users.activate') : t('users.deactivate')}
              </button>
            )}
            <button
              type="button"
              data-testid={`delete-user-${u.id}`}
              onClick={() => handleDelete(u)}
              disabled={isLoadingId(`delete-${u.id}`)}
              className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingId(`delete-${u.id}`) ? t('users.deleting') : t('common.delete')}
            </button>
          </span>
        )}
      />

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start md:items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div data-testid="user-modal" className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-4 sm:p-6 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto fade-in">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-xl sm:page-title text-2xl">
                {editingUser ? t('users.editUser') : t('users.addNewUser')}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingUser(null);
                  setFormData({
                    username: '',
                    password: '',
                    role: 'user',
                    level: 1,
                    tenant_id: user?.role === 'admin' ? user.tenant_id : ''
                  });
                }}
                className="text-gray-400 hover:text-gray-600 dark:text-surface-400 transition"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div>
                <label className="form-label">
                  {t('users.usernameRequired')}
                </label>
                <input
                  type="text"
                  data-testid="user-username-input"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  placeholder={t('users.usernamePlaceholder')}
                />
              </div>

              <div>
                <label className="form-label">
                  {editingUser ? t('users.passwordKeepCurrent') : t('users.passwordRequired')}
                </label>
                <input
                  type="password"
                  data-testid="user-password-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  placeholder={t('users.passwordPlaceholder')}
                />
              </div>

              {!editingUser && (
                <>
                  <div>
                    <label className="form-label">
                      {t('users.roleRequired')}
                    </label>
                    <select
                      data-testid="user-role-select"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    >
                      <option value="user">{t('users.role.user')}</option>
                      <option value="admin">{t('users.role.admin')}</option>
                    </select>
                  </div>

                  {user?.role === 'superuser' && (
                    <div>
                      <label className="form-label">
                        {t('users.companyRequired')}
                      </label>
                      <select
                        data-testid="user-company-select"
                        value={formData.tenant_id}
                        onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                        required
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                      >
                        <option value="">{t('users.selectCompany')}</option>
                        {companies.map((company) => (
                          <option key={company.id} value={company.id}>
                            {company.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}

              <div>
                <label className="form-label">
                  {t('users.fullName')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  placeholder={t('users.fullNamePlaceholder')}
                />
              </div>

              <div>
                <label className="form-label">
                  {t('users.email')}
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  placeholder={t('users.emailPlaceholder')}
                />
              </div>

              <div>
                <label className="form-label">
                  {formData.role === 'user' ? t('users.positionRequired') : t('users.position')}
                </label>
                <select
                  value={formData.position_id}
                  onChange={(e) => setFormData({ ...formData, position_id: e.target.value })}
                  required={formData.role === 'user'}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                >
                  <option value="">
                    {formData.role === 'user' ? t('users.selectPosition') : t('users.selectPositionOptional')}
                  </option>
                  {formPositions.length === 0 ? (
                    <option disabled>
                      {isSuperuser(user) && !formData.tenant_id ? t('users.selectCompanyFirst') : t('users.loadingPositions')}
                    </option>
                  ) : (
                    formPositions.map(pos => (
                      <option key={pos.id} value={pos.id}>{pos.name || pos.title}</option>
                    ))
                  )}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formData.role === 'user' 
                    ? t('users.positionHintUser')
                    : t('users.positionHintAdmin')}
                </p>
              </div>

              <div>
                <label className="form-label">
                  {t('users.level')}
                </label>
                <input
                  type="number"
                  data-testid="user-level-input"
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: parseInt(e.target.value) || 1 })}
                  min="1"
                  max="10"
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('users.levelHint')}</p>
              </div>

              <div className="flex gap-2 sm:gap-3 pt-4">
                <button
                  type="button"
                  data-testid="cancel-user-button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingUser(null);
                    setFormData({ username: '', password: '', role: 'user', level: 1, tenant_id: '', position_id: '', name: '', email: '' });
                  }}
                  disabled={submitting}
                  className="flex-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 text-gray-700 dark:text-gray-300 font-semibold py-2 sm:py-3 px-3 sm:px-4 text-sm sm:text-base rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  data-testid="save-user-button"
                  disabled={submitting}
                  className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 sm:py-3 px-3 sm:px-4 text-sm sm:text-base rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? t('users.saving') : (editingUser ? t('common.update') : t('common.create'))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersPage;
